import * as React from 'react';
import { useEffect, useState } from 'react';

import { useCollection } from '@amzn/awsui-collection-hooks';
import {
  Button,
  Checkbox,
  CollectionPreferences,
  CollectionPreferencesProps,
  Flashbar,
  Header,
  Pagination,
  Table,
  TextFilter,
} from '@amzn/awsui-components-react-v3';

import { scrollUp } from '../utils/navigation';

import {
  batchApprove,
  batchDeny,
  cancelRequest,
  getDataPermissionRequests,
  listFgaPolicies,
} from 'src/api/permissions';
import { EmptyState } from 'src/commons/EmptyState';
import {
  DenyOrCancelModal,
  extractAccessManagementInfoFromRequest,
  resourceBasedColumnDefinition,
} from 'src/components/requests/utils';
import {
  APPROVALS_STATUS_APPROVED,
  APPROVALS_STATUS_APPROVED_WITH_CONDITION,
  APPROVALS_STATUS_CANCELLED,
  APPROVALS_STATUS_REJECTED,
  STATUS_ACTIVE,
  TABLE_CONTENT_TYPE,
} from 'src/commons/constants';
import CsvDownloaderWrapper, { generateCsvColumnDefinitions } from 'src/components/common/csvDownloaderWrapper';
import {
  flattenRequestForCSV,
  generateCsvFileName,
  getCsvDataForDataRequest,
  getPermissionTypeForDPResource,
} from 'src/commons/common';
import _ from 'lodash';
import { PageHeader } from 'src/components/common/PageHeader';
import { BatchApproveResult, CancelRequest, GladstoneRequestItem } from 'aws-sdk/clients/awsdatalakegladstonelambda';

const DEFAULT_CANCEL_REASON = 'Please cancel the request.';

interface BrowseResourceBasedRequestsTable {
  isIncomingRequests: boolean;

  setContentType(contentType: string): void;

  activeGroup: string;
  activeWorkspace?: any;
  match?: any;
}

export interface FlatApprovalWorkflowItem {
  approvalTemplateId?: string;
  approvalWorkflowId?: string;
  approvalWorkflowStatus?: string;
  approvalDecisionTime?: string;
  omniTemplateId?: string;
  omniResourceId?: string;
  omniTemplateType?: string;
}

export interface FlatRequestItem {
  requestId?: string;
  status?: string;
  statusReason?: string;
  requestedOwnerId?: string;
  requestedTo?: string;
  requestedBy?: string;
  requestedTime?: string;
  permissionType?: string;
  simTickets?: string[];
  approvalsWorkflow?: FlatApprovalWorkflowItem[];
  requestContextIds?: string[];
  databaseName?: string;
  tableName?: string;
  catalogId?: string;
  clusterId?: string;
  redshiftWorkgroupName?: string;
  schemaName?: string;
  fgapName?: string;
  fgapId?: string;
  columnName?: string;
}

export interface ResourceBasedReqRecord extends FlatRequestItem {
  // only for incoming requests, when grouped by top level - ownerId + amazon approval, ownerId + request context, ownerId.
  groupId?: string;
  parentGroupId?: string;
}

export function BrowseResourceBasedRequestsTable({
  isIncomingRequests,
  setContentType,
  activeWorkspace,
}: BrowseResourceBasedRequestsTable) {
  const [requestsLoading, setRequestsLoading] = useState(true);
  const [allItems, setAllItems] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [columnDefinitions] = useState(resourceBasedColumnDefinition(isIncomingRequests));
  const [actionModalVisible, setActionModalVisible] = useState(false);
  const [actionRequestLoading, setActionRequestLoading] = useState(false);
  const [reasonOfAction, setReasonOfAction] = useState('');
  const [includesRequestHistory, setIncludesRequestHistory] = useState(false);
  const [action, setAction] = useState('');
  const [csvColumnDefinition, setCsvColumnDefinition] = useState([]);
  const [csvData, setCsvData] = useState([]);
  const [parentChildMap, setParentChildMap] = useState(new Map());
  const [allRequestItems, setAllRequestItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [expandedItems, setExpandedItems] = useState([]);

  const [preferences, setPreferences] = useState<CollectionPreferencesProps.Preferences>({
    wrapLines: false,
    pageSize: 15,
  });

  const refForCSVDownloader: React.RefObject<any> = React.createRef();
  const onClickDownload = () => {
    refForCSVDownloader.current.handleClick();
  };

  const { items, actions, filteredItemsCount, collectionProps, filterProps, paginationProps } = useCollection(
    allItems,
    {
      filtering: {
        empty: <EmptyState title='No requests' subtitle='No requests to display.' />,
        noMatch: (
          <EmptyState
            title='No matches'
            subtitle='We can’t find a match.'
            action={<Button onClick={() => actions.setFiltering('')}>Clear filter</Button>}
          />
        ),
      },
      pagination: { pageSize: preferences.pageSize },
      sorting: {},
      selection: {},
    },
  );

  // component did mount
  useEffect(() => {
    setContentType(TABLE_CONTENT_TYPE);
    const refresh = async () => {
      await handleRefresh();
    };
    refresh();
  }, []);

  // component did update
  useEffect(() => {
    const refresh = async () => {
      await handleRefresh();
    };
    refresh();
  }, [includesRequestHistory]);

  const transformData = (items, filteredFgaPolicies): FlatRequestItem[] => {
    const transformedData: FlatRequestItem[] = [];
    items.forEach((item) => {
      if (
        item.dataPermissionRequest.resource.lFTagPolicy == undefined &&
        item.dataPermissionRequest.resource.redshiftTagPolicy == undefined
      ) {
        let transform = {};
        transform['requestId'] = item.requestId;
        transform['status'] = item.status;
        transform['statusReason'] = item.statusReason || item.optimisticApprovalReason;
        if (isIncomingRequests) {
          transform['requestedOwnerId'] = item.ownerRequestedBy ? item.ownerRequestedBy : item.groupRequestedBy;
        } else {
          transform['requestedTo'] = item.ownerRequestedTo ? item.ownerRequestedTo : item.groupRequestedTo;
        }
        transform['requestedBy'] = item.requestedBy;
        transform['requestedTime'] = item.timeOfRequest;
        transform['permissionType'] = getPermissionTypeForDPResource(item.dataPermissionRequest.resource);
        transform = extractAccessManagementInfoFromRequest(item, transform);
        var resource;
        if (item.dataPermissionRequest.resource.tableWithColumns != undefined) {
          resource = item.dataPermissionRequest.resource.tableWithColumns;
        } else if (item.dataPermissionRequest.resource.table != undefined) {
          resource = item.dataPermissionRequest.resource.table;
        } else if (item.dataPermissionRequest.resource.database != undefined) {
          resource = item.dataPermissionRequest.resource.database;
        } else if (item.dataPermissionRequest.resource.dataCellsFilter != undefined) {
          resource = item.dataPermissionRequest.resource.dataCellsFilter;
        } else if (item.dataPermissionRequest.resource.dataZoneResource != undefined) {
          resource = item.dataPermissionRequest.resource.dataZoneResource;
        }
        transform['databaseName'] = resource.databaseName;
        // for non-DZ resource, the field is called name; for DZ it is called tableName.
        transform['tableName'] = resource.name ?? resource.tableName;
        // for non-DZ resource, the field is called catalogId; for DZ it is called awsAccountId.
        transform['catalogId'] = resource.catalogId ?? resource.awsAccountId;
        if (item.dataPermissionRequest.resource.dataCellsFilter != undefined) {
          transform['clusterId'] = resource.clusterId;
          transform['redshiftWorkgroupName'] = resource.redshiftWorkgroupName;
          transform['databaseName'] = resource.databaseName;
          transform['schemaName'] = resource.schemaName;
          transform['tableName'] = resource.tableName;
          transform['catalogId'] = resource.tableCatalogId;
          transform['fgapName'] = resource.name;
          for (const fgaPolicy of filteredFgaPolicies) {
            if (
              fgaPolicy.tableData.tableCatalogId == resource.tableCatalogId &&
              fgaPolicy.tableData.databaseName == resource.databaseName &&
              fgaPolicy.tableData.tableName == resource.tableName &&
              fgaPolicy.tableData.physicalName == resource.name
            ) {
              transform['fgapId'] = fgaPolicy.id;
              break;
            }
          }
        }
        if (resource?.columnNames != undefined) {
          transform['columnName'] = item.resource.columnNames.join(', ');
        }
        transformedData.push(transform);
      }
    });
    return transformedData;
  };

  const handleCancel = async () => {
    setActionRequestLoading(true);

    let finalCancelReason = DEFAULT_CANCEL_REASON;
    if (reasonOfAction !== '') {
      finalCancelReason = reasonOfAction;
    }
    const requests: CancelRequest[] = [];
    selectedItems.forEach((item) => {
      requests.push({
        requestId: item.requestId,
        reason: finalCancelReason,
      });
    });
    try {
      // No bulk operation, perform sequentially.
      for (const request of requests) {
        await cancelRequest(request);
      }
      await handleRefresh();
      successMessage('cancelled');
    } catch (err) {
      errorMessage(err.message, 'cancelling');
      console.log(err);
    }
    // clear selection
    setSelectedItems([]);
    setActionRequestLoading(false);
    setReasonOfAction('');
    setActionModalVisible(false);
    scrollUp();
  };

  async function getAllRequests(): Promise<{ filteredFgaPolicies: any[]; requests: GladstoneRequestItem[] }> {
    const requests = [];
    let nextToken = null;
    do {
      let dataPermissionAccessRequest = {
        workspaceId: activeWorkspace.workspaceId,
        isIncomingRequests: isIncomingRequests,
        includesRequestHistory: includesRequestHistory,
        nextToken: null,
      };
      if (nextToken != null) {
        dataPermissionAccessRequest['nextToken'] = nextToken;
      }
      var getNamedResourceAccessRequestResult = await getDataPermissionRequests(dataPermissionAccessRequest);
      nextToken = getNamedResourceAccessRequestResult?.nextToken;
      requests.push(...getNamedResourceAccessRequestResult?.requestList);
    } while (nextToken != null);
    // check if any FGA permission request present and retrieve the policies
    let filteredFgaPolicies = [];
    const fgapNameSet = new Set<string>();
    const ownerRequestedToSet = new Set<string>();
    for (const permissionRequest of requests) {
      if (permissionRequest?.dataPermissionRequest?.resource?.dataCellsFilter) {
        let dpRequest = permissionRequest.dataPermissionRequest;
        let fgapName: string = dpRequest.resource.dataCellsFilter.name;
        if (fgapName) fgapNameSet.add(fgapName);
        if (permissionRequest.ownerRequestedTo) ownerRequestedToSet.add(permissionRequest.ownerRequestedTo);
      }
    }
    let fgaPolicies = [];
    for (const ownerRequestedTo of ownerRequestedToSet) {
      console.log(`Fetching fgap policies for ownerId: ${ownerRequestedTo}`);
      let fgaPoliciesResult = await listFgaPolicies({
        ownerId: ownerRequestedTo,
        status: STATUS_ACTIVE,
      });
      fgaPolicies.push(...fgaPoliciesResult.FineGrainAccess);
      while (fgaPoliciesResult.nextToken) {
        fgaPoliciesResult = await listFgaPolicies({
          ownerId: ownerRequestedTo,
          status: STATUS_ACTIVE,
          nextToken: fgaPoliciesResult.nextToken,
        });
        fgaPolicies.push(...fgaPoliciesResult.FineGrainAccess);
      }
    }

    fgaPolicies.forEach((fgaPolicy) => {
      if (fgapNameSet.has(fgaPolicy.tableData.physicalName)) {
        filteredFgaPolicies.push(fgaPolicy);
      }
    });
    return { filteredFgaPolicies, requests };
  }

  function getParentGroupRecord(key: string, item: FlatRequestItem) {
    return { requestId: key, groupId: key, requestedTime: item.requestedTime, requestedOwnerId: item.requestedOwnerId };
  }

  function populateGroupingOfIncomingRequests(flatReqItems: FlatRequestItem[]) {
    const parentChildReqMap = new Map();
    // iterate over flatReqItems, group items by requestContextId only if requestContextIds size is 1,
    // or group by approvalsWorkflow.approvalWorkflowId, if approvalsWorkflow size is one else
    // group by requestedWorkspace
    const parentReqItemsMap: Map<string, ResourceBasedReqRecord> = new Map();
    const reqContextKey = 'Req Context';
    const approvalKey = 'Approval';
    flatReqItems.forEach((item) => {
      if (item?.requestContextIds?.length == 1) {
        const key = `${item.requestedOwnerId} - ${reqContextKey}: ${item.requestContextIds[0]}`;
        item['parentGroupId'] = key;
        if (parentChildReqMap.has(key)) {
          parentChildReqMap.get(key).push(item);
          // earliest requested time of all in the group.
          const parentItem = parentReqItemsMap.get(key);
          if (parentItem?.requestedTime > item?.requestedTime) {
            parentItem.requestedTime = item.requestedTime;
          }
        } else {
          parentReqItemsMap.set(key, getParentGroupRecord(key, item));
          parentChildReqMap.set(key, [item]);
        }
      } else if (item?.approvalsWorkflow?.length == 1 && item.approvalsWorkflow[0].approvalWorkflowId) {
        const key = `${item.requestedOwnerId} - ${approvalKey}: ${item.approvalsWorkflow[0].approvalWorkflowId}`;
        item['parentGroupId'] = key;
        if (parentChildReqMap.has(key)) {
          parentChildReqMap.get(key).push(item);
          // earliest requested time of all in the group.
          const parentItem = parentReqItemsMap.get(key);
          if (parentItem?.requestedTime > item?.requestedTime) {
            parentItem.requestedTime = item.requestedTime;
          }
        } else {
          parentReqItemsMap.set(key, getParentGroupRecord(key, item));
          parentChildReqMap.set(key, [item]);
        }
      } else {
        const key = item.requestedOwnerId;
        item['parentGroupId'] = key;
        if (parentChildReqMap.has(item.requestedOwnerId)) {
          parentChildReqMap.get(item.requestedOwnerId).push(item);
          // earliest requested time of all in the group.
          const parentItem = parentReqItemsMap.get(key);
          if (parentItem?.requestedTime > item?.requestedTime) {
            parentItem.requestedTime = item.requestedTime;
          }
        } else {
          parentReqItemsMap.set(key, getParentGroupRecord(key, item));
          parentChildReqMap.set(item.requestedOwnerId, [item]);
        }
      }
    });
    // remove single items from grouped items and move to common list.
    const singleGroupItems: ResourceBasedReqRecord[] = [];
    for (const [key, value] of parentChildReqMap.entries()) {
      if (key.includes(approvalKey) || key.includes(reqContextKey)) {
        //check if single item is present, then no grouping needed.
        if (value.length == 1) {
          singleGroupItems.push(value[0]);
          parentChildReqMap.delete(key);
          parentReqItemsMap.delete(key);
        }
      }
    }
    for (const reqItem of singleGroupItems) {
      let itemIncluded = false;
      for (const key of parentChildReqMap.keys()) {
        if (reqItem?.parentGroupId.includes(key)) {
          reqItem['parentGroupId'] = key;
          parentChildReqMap.get(key).push(reqItem);
          itemIncluded = true;
          break;
        }
      }
      if (!itemIncluded) {
        // add new entry for parent-child mapping.
        const key = reqItem.requestedOwnerId;
        reqItem['parentGroupId'] = key;
        parentChildReqMap.set(key, [reqItem]);
        parentReqItemsMap.set(key, getParentGroupRecord(key, reqItem));
      }
    }
    return { parentChildReqMap, parentReqItemsMap };
  }

  const getAllChildRequestItems = (item: ResourceBasedReqRecord): ResourceBasedReqRecord[] => {
    return item.groupId && parentChildMap && parentChildMap.has(item.groupId) ? parentChildMap.get(item.groupId) : [];
  };

  const expandableRows = isIncomingRequests
    ? {
        getItemChildren: getAllChildRequestItems,
        isItemExpandable: (item) => !!item.groupId && parentChildMap && parentChildMap.has(item.groupId),
        expandedItems: expandedItems,
        onExpandableItemToggle: ({ detail }) => {
          setExpandedItems((prev) => {
            const next = new Set((prev ?? []).map((item) => item.requestId));
            detail.expanded ? next.add(detail.item.requestId) : next.delete(detail.item.requestId);
            return [...next].map((requestId) => ({ requestId }));
          });
        },
      }
    : undefined;

  const handleRefresh = async () => {
    if (!activeWorkspace) return;
    setRequestsLoading(true);
    try {
      let { filteredFgaPolicies, requests } = await getAllRequests();
      let flatReqItems: FlatRequestItem[] = transformData(requests, filteredFgaPolicies);
      setAllRequestItems(flatReqItems);
      // Enable grouping only for incoming requests tab:  populate parent-child map.
      if (isIncomingRequests) {
        const { parentChildReqMap, parentReqItemsMap } = populateGroupingOfIncomingRequests(flatReqItems);
        setParentChildMap(parentChildReqMap);
        const groupParentRecords = [...parentReqItemsMap.values()];
        setAllItems(groupParentRecords);
      } else {
        setAllItems(flatReqItems);
      }

      if (requests) {
        let csvData = requests
          .map((item) => flattenRequestForCSV(item))
          .filter((item) => !_.isEmpty(item))
          .map((item) => getCsvDataForDataRequest(item));
        setCsvData(csvData);
        setCsvColumnDefinition(generateCsvColumnDefinitions(csvData));
      }
    } catch (err) {
      console.log(err.message);
      setRequestsLoading(false);
    }
    setRequestsLoading(false);
  };

  const displayNotification = (typeString: string, message: string) => {
    setNotifications([
      {
        type: typeString,
        content: message,
        dismissible: true,
        onDismiss: () => setNotifications([]),
      },
    ]);
  };

  const successMessage = (action) => {
    setNotifications([
      {
        type: 'success',
        content: `Data access request was '${action}'.`,
        dismissible: true,
        onDismiss: () => setNotifications([]),
      },
    ]);
  };

  const errorMessage = (message, action) => {
    setNotifications([
      {
        type: 'error',
        content: `There was an error '${action}' your request: '${message}'`,
        dismissible: true,
        onDismiss: () => setNotifications([]),
      },
    ]);
  };

  const handleApprove = async () => {
    setRequestsLoading(true);
    let requestList = [];
    selectedItems.forEach((item) =>
      requestList.push({
        requestId: item.requestId,
      }),
    );
    try {
      const approveResult: BatchApproveResult = await batchApprove({
        approveRequestList: requestList,
      });
      console.log('Batch approve results:', approveResult);
      const successfullyApprovedRequests = approveResult?.approveResultList?.filter(
        (item) =>
          // backward compatibility for response without succeeded field
          item.succeeded || item.message?.includes('SUCCEEDED'),
      );

      await handleRefresh();
      if (successfullyApprovedRequests?.length > 0) {
        displayNotification(
          'success',
          `Successfully approved ${successfullyApprovedRequests.length} out of ${selectedItems.length} requests!`,
        );
      } else {
        displayNotification(
          'error',
          `Failed to approve ${selectedItems.length} requests! Check status message in corresponding requests.`,
        );
      }
      // clear selection for succeeded ones.
      setSelectedItems(
        selectedItems.filter((item) => !successfullyApprovedRequests?.some((s) => s.requestId === item.requestId)),
      );
    } catch (err) {
      console.log(err);
      await handleRefresh();
      // handle timeout and display different message
      // timeout message: Endpoint request timed out / Network Failure
      if (
        err.statusCode === 504 ||
        err.message?.toLowerCase().includes('timed out') ||
        err.message?.toLowerCase().includes('network failure')
      ) {
        displayNotification('info', 'Request is still being processed! Please check back after few minutes.');
      } else {
        errorMessage(err.message, 'approving');
      }
      // clear all selection, unable to filter pending requests.
      setSelectedItems([]);
    }
    setRequestsLoading(false);
  };

  const handleDeny = async () => {
    setRequestsLoading(true);
    let requestList = [];
    let finalDenyReason = 'Denied by data owner!';
    if (reasonOfAction !== '') {
      finalDenyReason = reasonOfAction;
    }
    selectedItems.forEach((item) =>
      requestList.push({
        requestId: item.requestId,
        reason: finalDenyReason,
      }),
    );
    try {
      await batchDeny({
        denyRequestList: requestList,
      });
      await handleRefresh();
      successMessage('denied');
    } catch (err) {
      console.log(err);
      await handleRefresh();
      // handle timeout and display different message
      // timeout message: Endpoint request timed out / Network Failure
      if (
        err.statusCode === 504 ||
        err.message?.toLowerCase().includes('timed out') ||
        err.message?.toLowerCase().includes('network failure')
      ) {
        displayNotification('info', 'Request is still being processed! Please check back after few minutes.');
      } else {
        errorMessage(err.message, 'denying');
      }
    }
    // clear selection
    setSelectedItems([]);
    setRequestsLoading(false);
    setReasonOfAction('');
  };

  const handleAction = async (e) => {
    setAction(e.detail.id);
    if (e.detail.id == 'cancel' || e.detail.id == 'deny') {
      setActionModalVisible(true);
    } else {
      await handleApprove();
    }
  };

  const shouldDisableDenyOrCancelButton = () => {
    // if no items are selected, no action can be taken
    if (!selectedItems) return true;
    if (selectedItems.length === 0) return true;
    // if any request is not pending, no action can be taken
    selectedItems.forEach((item) => {
      if (item.status != 'PENDING') return true;
    });
    return false;
  };

  const shouldDisableApproveButton = () => {
    // if no items are selected, no action can be taken
    if (!selectedItems) return true;
    if (selectedItems.length === 0) return true;
    // if any request is not pending, no action can be taken
    selectedItems.forEach((item) => {
      if (item.status != 'PENDING') return true;
    });
    // if any approval workflow is not approved, no action can be taken
    let anyApprovalWorkflowsAreNotApproved = false;
    for (const item of selectedItems) {
      if (item.approvalsWorkflow) {
        for (const approvalWorkflow of item.approvalsWorkflow) {
          const status = approvalWorkflow.approvalWorkflowStatus;
          if (status != APPROVALS_STATUS_APPROVED && status != APPROVALS_STATUS_APPROVED_WITH_CONDITION) {
            anyApprovalWorkflowsAreNotApproved = true;
            break;
          }
        }
      }
      if (anyApprovalWorkflowsAreNotApproved) break;
    }
    return anyApprovalWorkflowsAreNotApproved;
  };

  const options = () => {
    return [
      {
        text: '',
        icon: 'refresh',
        onItemClick: handleRefresh,
      },
      {
        text: 'Actions',
        onItemClick: handleAction,
        loading: requestsLoading,
        items: isIncomingRequests
          ? [
              {
                text: shouldDisableApproveButton()
                  ? `Approve (${selectedItems.length}, has pending approvals)`
                  : `Approve (${selectedItems.length})`,
                id: 'approve',
                disabled: shouldDisableApproveButton(),
              },
              {
                text: `Deny (${selectedItems.length})`,
                id: 'deny',
                disabled: shouldDisableDenyOrCancelButton(),
              },
            ]
          : [
              {
                text: `Cancel (${selectedItems.length})`,
                id: 'cancel',
                disabled: shouldDisableDenyOrCancelButton(),
              },
            ],
      },
    ];
  };

  const closeActionModal = () => {
    setActionModalVisible(false);
  };

  const submitModalAction = async (action) => {
    setActionModalVisible(false);
    if (action === 'cancel') {
      await handleCancel();
    } else if (action === 'approve') {
      await handleApprove();
    } else if (action === 'deny') {
      await handleDeny();
    }
  };

  const approvalWorkflowStatusOutOfSync = (desiredStatuses: string[]) => {
    let outOfSync = false;
    selectedItems.forEach((item) => {
      item['approvalsWorkflow']?.forEach((workflowItem) => {
        if (!desiredStatuses.includes(workflowItem.approvalWorkflowStatus)) {
          outOfSync = true;
        }
      });
    });
    return outOfSync;
  };

  return (
    <>
      <Flashbar items={notifications} />
      <Table
        {...collectionProps}
        loadingText='Loading requests...'
        columnDefinitions={columnDefinitions}
        items={items}
        wrapLines={false}
        resizableColumns={true}
        expandableRows={expandableRows}
        header={
          <>
            <PageHeader
              buttons={options()}
              header={
                <>
                  <Header
                    counter={
                      isIncomingRequests
                        ? `(${items.length} of ${allItems.length} groups, total requests: ${allRequestItems.length})`
                        : `(${items.length} of ${allItems.length} requests)`
                    }
                  >
                    Resource-based access requests
                  </Header>
                  <Checkbox
                    onChange={({ detail }) => setIncludesRequestHistory(detail.checked)}
                    checked={includesRequestHistory}
                  >
                    Show request history
                  </Checkbox>
                </>
              }
              additionalItems={
                !requestsLoading &&
                csvData.length > 0 && (
                  <>
                    <Button variant='primary' onClick={onClickDownload} iconName='download'>
                      Download as CSV
                    </Button>
                    <CsvDownloaderWrapper
                      rowItems={csvData}
                      fileName={generateCsvFileName('requests', activeWorkspace ? activeWorkspace.workspaceId : null)}
                      refForCsvDownloader={refForCSVDownloader}
                      userFriendlyDataDefinitions={csvColumnDefinition}
                    />
                  </>
                )
              }
            />
          </>
        }
        empty={<EmptyState title='No requests' subtitle='No requests were found.' />}
        loading={requestsLoading}
        preferences={
          <CollectionPreferences
            title='Preferences'
            confirmLabel='Confirm'
            cancelLabel='Cancel'
            preferences={preferences}
            onConfirm={({ detail }) => setPreferences(detail)}
            pageSizePreference={{
              title: 'Page size',
              options: [
                { value: 25, label: '25 items' },
                { value: 50, label: '50 items' },
                { value: 100, label: '100 items' },
              ],
            }}
            wrapLinesPreference={{
              label: 'Wrap lines',
              description: 'Enable to wrap table cell content, disable to truncate text.',
            }}
          />
        }
        selectionType={'multi'}
        trackBy='requestId'
        onSelectionChange={({ detail }) => {
          setSelectedItems(detail.selectedItems);
        }}
        selectedItems={selectedItems}
        isItemDisabled={(item) => item.status != 'PENDING' && item.status != 'FAILED'}
        filter={
          <TextFilter
            {...filterProps}
            filteringPlaceholder='Find resources'
            filteringAriaLabel='Filter named resource access requests'
            countText={`${filteredItemsCount} ${filteredItemsCount > 1 ? 'matches' : 'match'}`}
          />
        }
        pagination={<Pagination {...paginationProps} />}
      ></Table>
      <DenyOrCancelModal
        modalVisible={actionModalVisible}
        action={action}
        closeModal={closeActionModal}
        buttonLoading={actionRequestLoading}
        submitAction={submitModalAction}
        approvalWorkflowStatusOutOfSync={approvalWorkflowStatusOutOfSync([
          APPROVALS_STATUS_REJECTED,
          APPROVALS_STATUS_CANCELLED,
        ])}
        textAreaPlaceholder={''}
        reasonOfAction={reasonOfAction}
        setReasonOfAction={setReasonOfAction}
      />
    </>
  );
}
